import {DocumentPreview} from "@/components/ui/forms/DocumentPreview";
import {MaskList} from "@/components/ui/forms/MaskList";
import {RadioGroup} from "@/components/ui/inputs/RootRadio";
import {checkNameTaken, isEqual} from "@/helpers";
import {useCriteriaOptions, usePhaseOptions} from "@/hooks/ScenarioOptions";
import {DoctorField} from "@/pages/admin/DoctorPage/DoctorCreatePage";
import {viewDoctor} from "@/redux/slices/doctorSlices/viewSlice";
import {
  clearEdit,
  getEditScenario,
  setError,
  setIsChanged,
  setTarget,
  updateScenario,
} from "@/redux/slices/scenarioSlices/editSlice";

import React, {useCallback, useEffect, useRef, useState} from "react";

import check from "@images/badges/check.png";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {FillDataPopup} from "@popups/attentionPopups/FillDataPopup";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import {UploadImagePopup} from "@popups/uploadPopups/UploadImagePopup";
import {BackButton} from "@ui/buttons/BackButton";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {EditField} from "@ui/forms/EditField";
import {EditTitle} from "@ui/forms/EditTitle";
import {ViewField} from "@ui/forms/ViewField";
import {BorderArticle} from "@ui/general/BorderArticle";
import {PreviewCard} from "@ui/general/PreviewCard";
import {Spinner} from "@ui/general/Spinner";
import {SCENARIO_CERTIFICATION_BADGES} from "@variables";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";

import styles from "./styles.module.scss";

export const ScenarioEditPage = () => {
  const {id} = useParams();
  const dispatch = useDispatch();
  const scenarioRef = useRef();
  const {openPopup} = usePopup();
  const {t} = useTranslation();

  const {isMobile, isMayoly} = useSelector((state) => state.common.settings);
  const {target, initialTarget, errors, status} = useSelector(
    (state) => state.scenario.edit,
  );
  const viewDoctorState = useSelector((state) => state.doctor.view);

  const [photoFile, setPhotoFile] = useState(null);

  const criteriaOptions = useCriteriaOptions(target);
  const phaseOptions = usePhaseOptions();

  const updateScenarioField = useCallback(
    (fieldType, value) => {
      dispatch(
        setTarget({
          ...target,
          [fieldType]: value,
        }),
      );
    },
    [target, dispatch],
  );

  const handleClickCover = useCallback(
    (color) => {
      updateScenarioField("mask", color);
    },
    [updateScenarioField],
  );

  const handleClickUploadImage = () => {
    openPopup(<UploadImagePopup setPhotoFile={setPhotoFile} />);
  };

  const handleClickRemovePhoto = () => {
    setPhotoFile(null);
  };

  const handleClickSave = async () => {
    const validation = [
      target?.name === "",
      target?.mask === "",
      target?.purpose === "",
      target?.objectives === "",
      target?.due_date === "",
    ];

    if (target?.certification) {
      validation.push(target?.badge === "");
      validation.push(target?.start_date === "");
      validation.push(target?.finish_date === "");
    }

    if (validation.some((item) => item)) {
      openPopup(<FillDataPopup />);
    } else {
      const scenario = {
        scenario: {
          name: target?.name,
          mask: target?.mask,
          badge: target?.badge,
          due_date: target?.due_date,
          start_date: target?.start_date,
          finish_date: target?.finish_date,
          purpose: target?.purpose,
          objectives: target?.objectives,
          key_messages: target?.key_messages,
          keywords: target?.keywords,
          structure: target?.structure,
          script: target?.script,
          phase: target?.phase,
          questions: target?.questions,
          criteria_weights: target?.criteria_weights,
          certification: target?.certification,
          cover: photoFile,
        },
      };
      const resultAction = await dispatch(updateScenario({id, scenario}));

      if (updateScenario.fulfilled.match(resultAction)) {
        openPopup(
          <SuccessfulSavePopup title={t("popup.save_data.scenario")} />,
        );

        dispatch(setIsChanged(false));
        setPhotoFile(null);
      } else {
        dispatch(setError("Save error!"));
      }
    }
  };

  useEffect(() => {
    dispatch(getEditScenario({id}));

    return () => {
      dispatch(clearEdit());
    };
  }, [dispatch]);

  useEffect(() => {
    if (status !== "success" || !target?.doctor_profile_id) return;
    dispatch(viewDoctor({id: target.doctor_profile_id}));
  }, [dispatch, status, target?.doctor_profile_id]);

  useEffect(() => {
    dispatch(setIsChanged(!isEqual(target, initialTarget)));
  }, [target, initialTarget, dispatch]);

  useEffect(() => {
    return () => {
      if (photoFile) {
        URL.revokeObjectURL(photoFile);
      }
    };
  }, [photoFile]);

  useEffect(() => {
    if (scenarioRef.current && isMobile) {
      scenarioRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [isMobile]);

  return (
    <div
      ref={scenarioRef}
      className={styles.scenarioWrapper}
    >
      <BorderArticle>
        <div className={styles.scenario}>
          <BackButton />

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.scenarioContent}>
              <div className={styles.scenarioEditContent}>
                <div className={styles.scenarioContentHeadWrapper}>
                  <div className={styles.scenarioContentHead}>
                    <EditTitle
                      label={"scenario-name"}
                      value={target?.name}
                      placeholder={`${t("input.scenario_name_placeholder")}`}
                      disabledButton={false}
                      handleClickSave={() => handleClickSave()}
                      onChange={(e) =>
                        updateScenarioField("name", e.target.value)
                      }
                      error={
                        errors && checkNameTaken(errors)
                          ? t("server_error.exist_name")
                          : null
                      }
                    />

                    <ViewField
                      label={`${t("input.background")}*`}
                      customContent={
                        <MaskList
                          onClickCover={(color) => handleClickCover(color)}
                        />
                      }
                    />

                    <ViewField
                      label={t("input.cover")}
                      customContent={
                        <div
                          className={"flex flex-col items-start gap-[16rem]"}
                        >
                          {photoFile && (
                            <DocumentPreview
                              name={photoFile?.name}
                              onClickCross={() => handleClickRemovePhoto()}
                            />
                          )}

                          <ControlButton
                            color={"var(--violetLight)"}
                            txt={t("button.upload_image")}
                            onClick={() => handleClickUploadImage()}
                          />
                        </div>
                      }
                    />
                  </div>

                  <ViewField
                    label={t("input.preview")}
                    value={target?.name}
                    direction={"col"}
                    customContent={
                      <PreviewCard
                        name={target?.name}
                        mask={target?.mask}
                        image={
                          photoFile ? URL.createObjectURL(photoFile) : undefined
                        }
                      />
                    }
                  />
                </div>
              </div>

              <ul
                className={`${styles.scenarioContentBody} ${styles.scenarioEditBody}`}
              >
                <EditField
                  label={t("input.is_certification")}
                  checked={target?.certification}
                  onChange={(e) =>
                    updateScenarioField("certification", e.target.checked)
                  }
                  type={"checkbox"}
                />

                {target?.certification && (
                  <EditField
                    label={`${t("input.date_range")}*`}
                    fromDate={
                      target?.start_date
                        ? typeof target.start_date === "string"
                          ? new Date(target.start_date)
                          : target.start_date
                        : new Date()
                    }
                    setFromDate={(date) => {
                      updateScenarioField(
                        "start_date",
                        date instanceof Date ? date.toISOString() : date,
                      );
                    }}
                    toDate={
                      target?.finish_date
                        ? typeof target.finish_date === "string"
                          ? new Date(target.finish_date)
                          : target.finish_date
                        : new Date()
                    }
                    setToDate={(date) => {
                      updateScenarioField(
                        "finish_date",
                        date instanceof Date ? date.toISOString() : date,
                      );
                    }}
                    type={"range_date"}
                  />
                )}

                <EditField
                  label={`${t("input.period_of_action")}*`}
                  setDate={(date) =>
                    updateScenarioField(
                      "due_date",
                      date instanceof Date ? date.toISOString() : date,
                    )
                  }
                  date={target?.due_date}
                  type={"date"}
                />

                {!isMayoly && (
                  <ViewField
                    label={t("input.doctor")}
                    customContent={
                      target?.doctor_profile_id ? (
                        <div className={"flex items-center gap-[16rem]"}>
                          <RootDesc>
                            <span style={{padding: "4rem 8rem"}}>
                              {viewDoctorState.doctor?.id ==
                                target.doctor_profile_id &&
                                viewDoctorState.doctor?.name}
                            </span>
                          </RootDesc>
                          <DoctorField
                            doctorProfileId={target?.doctor_profile_id}
                          />
                        </div>
                      ) : (
                        <span style={{color: "var(--grey)"}}>-</span>
                      )
                    }
                  />
                )}

                <ViewField
                  label={`${t("input.related_products")}*`}
                  customContent={
                    <RootDesc>
                      <span
                        className={"block p-[4rem_8rem]"}
                        style={{color: "var(--grey)"}}
                      >
                        {target?.products?.length
                          ? target.products
                              .map((p) => p?.name)
                              .filter(Boolean)
                              .join(", ")
                          : "-"}
                      </span>
                    </RootDesc>
                  }
                />

                <EditField
                  label={`${t("input.purpose")}*`}
                  value={target?.purpose}
                  onChange={(e) =>
                    updateScenarioField("purpose", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.objectives")}*`}
                  value={target?.objectives}
                  onChange={(e) =>
                    updateScenarioField("objectives", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.messages")}*`}
                  value={target?.key_messages}
                  onChange={(e) =>
                    updateScenarioField("key_messages", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.words")}*`}
                  value={target?.keywords}
                  onChange={(e) =>
                    updateScenarioField("keywords", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.structure")}*`}
                  value={target?.structure}
                  onChange={(e) =>
                    updateScenarioField("structure", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.script")}*`}
                  value={target?.script}
                  onChange={(e) =>
                    updateScenarioField("script", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={`${t("input.objections")}*`}
                  value={target?.questions}
                  onChange={(e) =>
                    updateScenarioField("questions", e.target.value)
                  }
                  type={"textarea"}
                />

                <EditField
                  label={t("input.materials")}
                  type={"custom"}
                  customContent={
                    target?.document_files && target?.document_files.length > 0
                      ? target?.document_files.map((file) => (
                          <React.Fragment key={file?.sha1}>
                            <DocumentPreview name={file?.file?.filename} />
                          </React.Fragment>
                        ))
                      : "-"
                  }
                />

                <EditField
                  label={`${t("input.phase")}*`}
                  value={target?.phase}
                  type={"custom"}
                  customContent={
                    <RadioGroup
                      name={"phase"}
                      className={styles.radioGroup}
                      options={phaseOptions}
                      value={target?.phase}
                      onChange={(option) => {
                        const phaseValue = option?.value || option;
                        updateScenarioField("phase", phaseValue);
                      }}
                    />
                  }
                />

                <EditField
                  label={`${t("input.criteria")}*`}
                  type={"custom"}
                  customContent={
                    <ul className={styles.criteriaContent}>
                      {criteriaOptions &&
                        criteriaOptions.length > 0 &&
                        criteriaOptions.map((item) => (
                          <li key={item.id}>
                            <RootDesc>
                              <b
                                dangerouslySetInnerHTML={{__html: item.label}}
                              />
                            </RootDesc>

                            <ul>
                              {item.rates.map((rate) => (
                                <li key={rate.id}>
                                  <SmallDesc>
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: rate.label,
                                      }}
                                    />
                                  </SmallDesc>

                                  <RadioGroup
                                    rating
                                    ratingType={"bullet"}
                                    required={false}
                                    name={item.value}
                                    value={rate.value}
                                    onChange={(ratingValue) => {
                                      const currentCriteria =
                                        target?.criteria_weights || {};
                                      const updatedCriteria = {
                                        ...currentCriteria,
                                        [item.value]: {
                                          ...currentCriteria[item.value],
                                          [rate.key]: ratingValue,
                                        },
                                      };

                                      dispatch(
                                        setTarget({
                                          ...target,
                                          criteria_weights: updatedCriteria,
                                        }),
                                      );
                                    }}
                                  />
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                    </ul>
                  }
                />

                {target?.certification && (
                  <EditField
                    label={`${t("input.badge")}*`}
                    value={target?.badge}
                    onChange={(e) =>
                      updateScenarioField("badge", e.target.value)
                    }
                    type={"custom"}
                    customContent={
                      <ul className={styles.badgeContent}>
                        {SCENARIO_CERTIFICATION_BADGES.map((badge) => (
                          <li
                            onClick={() =>
                              updateScenarioField("badge", badge.name)
                            }
                            style={
                              target?.badge === badge.name
                                ? {
                                    transform: "scale(1.1)",
                                    cursor: "default",
                                    pointerEvents: "none",
                                    boxShadow:
                                      "0 -10rem 10rem 0 rgba(180, 0, 226, 0.1) inset, 0 10rem 10rem 0 rgba(180, 0, 226, 0.1)",
                                  }
                                : {cursor: "pointer"}
                            }
                            key={badge.id}
                          >
                            <img
                              src={badge.image}
                              alt={badge.name}
                            />

                            <i
                              style={
                                target?.badge === badge.name
                                  ? {
                                      opacity: 1,
                                      transform: "scale(1)",
                                      right: "0",
                                      top: "0",
                                    }
                                  : {}
                              }
                            >
                              <img
                                src={check}
                                alt={"check"}
                              />
                            </i>
                          </li>
                        ))}
                      </ul>
                    }
                  />
                )}
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
